import { useState, useEffect } from 'react';
import {
  Copy,
  Check,
  FileDown,
  FileSpreadsheet,
  Presentation,
  RotateCcw,
  Share2,
  Link2,
} from 'lucide-react';
import { copyRich, copyPlain } from '../../lib/clipboard';

interface ResultsActionBarProps {
  copyLabel?: string;
  copyText?: string;
  /** Optional HTML variant for Copy All — when present we paste rich + plain text */
  copyHtml?: string;
  showCopy?: boolean;
  showCsv?: boolean;
  onExportCsv?: () => void;
  showPdf?: boolean;
  onDownloadPdf?: () => void;
  pdfLoading?: boolean;
  showPresenter?: boolean;
  onPresenterMode?: () => void;
  onStartOver: () => void;
  showShare?: boolean;
  onShare?: () => void;
  isShared?: boolean;
  shareLoading?: boolean;
}

const hidden = 'invisible pointer-events-none';

export function ResultsActionBar({
  copyLabel = 'Copy All',
  copyText = '',
  copyHtml,
  showCopy = true,
  showCsv = false,
  onExportCsv,
  showPdf = true,
  onDownloadPdf,
  pdfLoading = false,
  showPresenter = false,
  onPresenterMode,
  onStartOver,
  showShare = false,
  onShare,
  isShared = false,
  shareLoading = false,
}: ResultsActionBarProps) {
  const [copied, setCopied] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [copyText, copyHtml]);

  const handleCopy = async () => {
    const ok = copyHtml
      ? await copyRich({ html: copyHtml, text: copyText })
      : await copyPlain(copyText);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4">
      {/* Left group: Copy + PDF/CSV */}
      <div className="flex items-center gap-2">
        {/* Copy All — solid */}
        <button
          onClick={handleCopy}
          className={`inline-flex items-center justify-center gap-2 h-10 px-4 md:min-w-[180px] text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
            copied
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 hover:border-slate-600'
          } ${!showCopy ? hidden : ''}`}
          aria-hidden={!showCopy}
          tabIndex={showCopy ? 0 : -1}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          <span className="hidden md:inline">{copied ? 'Copied!' : copyLabel}</span>
        </button>

        {/* PDF — gradient fill */}
        <button
          onClick={onDownloadPdf}
          disabled={pdfLoading}
          className={`inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 border border-white/10 shadow-md shadow-blue-900/30 hover:brightness-110 hover:shadow-blue-500/30 active:brightness-95 transition-all duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${!showPdf ? hidden : ''}`}
          aria-hidden={!showPdf}
          tabIndex={showPdf ? 0 : -1}
        >
          <FileDown className={`w-4 h-4 ${pdfLoading ? 'animate-pulse' : ''}`} />
          <span className="hidden md:inline">{pdfLoading ? 'Generating\u2026' : 'PDF'}</span>
        </button>

        {/* Share Script — outline/ghost */}
        <button
          onClick={() => {
            onShare?.();
            setShareFeedback(true);
            setTimeout(() => setShareFeedback(false), 2500);
          }}
          disabled={shareLoading}
          className={`inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
            shareFeedback
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : isShared
                ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20'
                : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 hover:border-slate-600'
          } ${!showShare ? hidden : ''} disabled:opacity-50`}
          aria-hidden={!showShare}
          tabIndex={showShare ? 0 : -1}
        >
          {shareLoading ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : shareFeedback ? (
            <Check className="w-4 h-4" />
          ) : isShared ? (
            <Link2 className="w-4 h-4" />
          ) : (
            <Share2 className="w-4 h-4" />
          )}
          <span className="hidden md:inline">
            {shareLoading ? 'Sharing...' : shareFeedback ? 'Link Copied' : isShared ? 'Copy Link' : 'Share Script'}
          </span>
        </button>

        {/* CSV — solid */}
        <button
          onClick={onExportCsv}
          className={`inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-medium rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 hover:border-slate-600 transition-colors whitespace-nowrap ${!showCsv ? hidden : ''}`}
          aria-hidden={!showCsv}
          tabIndex={showCsv ? 0 : -1}
        >
          <FileSpreadsheet className="w-4 h-4" />
          <span className="hidden md:inline">CSV</span>
        </button>
      </div>

      {/* Right group: Present + Start Over */}
      <div className="flex items-center gap-2">
        {/* Present — premium gradient fill */}
        <button
          onClick={onPresenterMode}
          className={`inline-flex items-center justify-center gap-2 h-10 px-6 md:min-w-[140px] text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 border border-white/10 shadow-lg shadow-purple-500/25 hover:brightness-110 hover:shadow-purple-500/40 active:brightness-95 transition-all duration-200 whitespace-nowrap ${!showPresenter ? hidden : ''}`}
          aria-hidden={!showPresenter}
          tabIndex={showPresenter ? 0 : -1}
        >
          <Presentation className="w-4 h-4" />
          <span className="hidden md:inline">Present</span>
        </button>

        {/* Start Over — solid with danger hover */}
        <button
          onClick={onStartOver}
          className="inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-medium rounded-lg bg-slate-800 hover:bg-red-900/50 text-slate-200 hover:text-red-200 border border-slate-700 hover:border-red-700/50 transition-colors whitespace-nowrap"
        >
          <RotateCcw className="w-4 h-4" />
          <span className="hidden md:inline">Start Over</span>
        </button>
      </div>
    </div>
  );
}
